<template>
  <v-row
    no-gutters
    class="annotation-image-card flex-column fill-height"
  >
    <v-row
      no-gutters
      align="center"
      justify="center"
      class="image-component fill-height"
    >
      <div
        class="image-component__container"
        no-gutters
      >
        <v-img
          v-if="image"
          :src="image"
          max-height="100%"
          class="pointer"
          position="center center"
          contain
          :disable="true"
          @error="isImageError = true"
          @click="openModal"
        />
        <div
          v-else
          class="annotation-image-card__mural"
        >
          <img :src="muralIcon">
          <DsButton
            v-if="isEditor"
            class="text-truncate"
            color="secondary"
            @click="openModal"
          >
            {{ $t('components.AnnotationImageCard.label.insertImage') }}
          </DsButton>
        </div>
      </div>
      <div
        v-if="showOptionsButton"
        class="image-component__options-button pl-2 py-4 pr-4"
      >
        <OptionsButton
          :options-data="optionsData"
          :expanded-mode="card.props.isExpandedCard"
          @on-option-menu-click="callOptionMenu"
        />
      </div>
    </v-row>

    <div
      v-if="modal"
      class="annotation-image-card__modal"
    >
      <div class="annotation-image-card__tabs">
        <v-tabs
          v-model="tab"
          background-color="transparent"
          height="36"
          grow
        >
          <v-tab>
            {{ $t('components.AnnotationImageCard.label.insertFile') }}
          </v-tab>
          <v-tab
            class="annotation-image-card__link"
          >
            {{ $t('components.AnnotationImageCard.label.insertLink') }}
          </v-tab>
        </v-tabs>
      </div>
      <v-text-field
        v-if="isLink"
        v-model="imageLink"
        outlined
        clear-icon="mdi-close"
        clearable
        dense
        :label="$t('components.AnnotationImageCard.label.link')"
      />
      <div class="annotation-image-card__button">
        <button
          class="annotation-image-card__cancel-button"
          type="button"
          @click="cancelChanges"
        >
          {{ $t('components.AnnotationImageCard.label.cancel') }}
        </button>
        <DsButton
          v-if="isLink"
          size="dense"
          :disabled="disableButton"
          @click="saveImage"
        >
          {{ $t('components.AnnotationImageCard.label.select') }}
        </DsButton>
        <DsButton
          v-else
          class="annotation-image-card__select-file"
          size="dense"
        >
          <label>
            {{ $t('components.AnnotationImageCard.label.select') }}
            <input
              type="file"
              accept="image/png, image/jpeg"
              @change="previewFiles"
            >
          </label>
        </DsButton>
      </div>
    </div>
  </v-row>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import cardsModel from '@/mixins/cards-model';
import { DsButton } from '@ds/components';
import mural from '@/assets/img/mural.svg';
import { trackEventWithUserProperties } from '@/utils/mixpanel';
import OptionsButton from '@/components/OptionsButton';

const STORAGE_IMAGE = 'firebasestorage';

export default {
  name: 'AnnotationImageCard',
  components: {
    DsButton,
    OptionsButton,
  },
  mixins: [
    cardsModel,
  ],
  props: {
    itemId: {
      type: [String, Number],
      required: true,
    },
    title: {
      type: [String, Number],
      default: '',
    },
  },
  data() {
    return {
      tab: null,
      imageLink: '',
      previousImage: '',
      image: '',
      muralIcon: mural,
      inputLoading: false,
      modal: false,
      isImageError: false,
    };
  },
  computed: {
    ...mapGetters('user', [
      'isEditor',
    ]),
    isLink() {
      return Boolean(this.tab === 1);
    },
    disableButton() {
      return Boolean(!this.imageLink);
    },
    optionsData() {
      return this.baseCardOptions.filter(({ iconname }) => iconname !== 'filter');
    },
    showOptionsButton() {
      return Boolean(this.optionsData.length);
    },
  },
  mounted() {
    if (this.card) {
      this.isFirebaseLink();

      this.previousImage = this.card.props.image;
      this.image = this.previousImage;
      return true;
    }

    console.error('Could not find the card', this.itemId);
  },
  methods: {
    ...mapActions('dashboard', [
      'uploadImage',
      'downloadImage',
      'deleteImage',
    ]),
    async saveImage() {
      this.closeModal();
      if (this.isLink) {
        const isValid = await this.isValidImgUrl(this.imageLink);
        this.image = isValid ? this.imageLink : '';
        this.trackMixPanel('Link');

        if (this.card.props.hasUpload) {
          this.deleteImage(this.card.id);
          this.card.props.hasUpload = false;
        }
      }

      this.previousImage = this.image;

      this.inputLoading = true;
      setTimeout(() => {
        this.card.props.image = this.image;
        this.saveCards().then(() => {
          this.inputLoading = false;
        });
      }, 100);
    },
    cancelChanges() {
      this.image = this.card.props.image;
      this.isFirebaseLink();
      this.closeModal();
    },
    isValidImgUrl(url) {
      const img = new Image();
      img.src = url;
      return new Promise((resolve) => {
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
      });
    },
    openModal() {
      if (this.isEditor) {
        this.modal = true;
      }
    },
    closeModal() {
      this.modal = false;
    },
    async previewFiles(event) {
      const obj = {
        event,
        cardId: this.card.id,
      };

      this.image = await this.uploadImage(obj);
      this.imageLink = '';
      this.card.props.hasUpload = true;
      this.card.props.image = this.image;

      this.saveImage();
      this.trackMixPanel('File');

      this.closeModal();
    },
    isFirebaseLink() {
      this.imageLink = !this.card.props.image?.includes(STORAGE_IMAGE) ? this.card.props.image : '';
    },
    trackMixPanel(imageType) {
      trackEventWithUserProperties('ST Card Image Add', { type: imageType });
    },
  },
};
</script>

<style scoped lang="scss">
  .annotation-image-card {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--ds-spacing-stack-nano);

    &:hover {
      background-color: var(--ds-color-overlay);
    }

    &__mural {
      display: flex;
      flex-direction: column;
      width: fit-content;

      img {
        margin-bottom: var(--ds-spacing-inline-nano);
      }
    }

    &__modal {
      position: absolute;
      width: 300px;
      background: var(--ds-color-surface-contrast);
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-direction: column;
      box-shadow: 0 2px var(--ds-spacing-inline-sm) -6px rgba(0, 0, 0, 0.32), 0 2px 64px -4px rgba(0, 0, 0, 0.32);
      padding: var(--ds-spacing-stack-xs);
      border-radius: var(--ds-border-radius-sm);
    }

    &__tabs {
      width: 100%;
      margin-bottom: var(--ds-spacing-inline-sm);
    }

    &__button {
      display: flex;
      gap: var(--ds-spacing-stack-xs);
    }

    &__select-file {
      label {
        cursor: pointer;

        input {
          display: none;
        }
      }
    }

    .ds-button:disabled {
      cursor: not-allowed;
    }

    &__cancel-button {
      font-weight: var(--ds-font-weight-semibold);
      font-size: var(--ds-font-size-xs);
      line-height: var(--ds-font-line-height-dense);
      color: var(--ds-color-text-interactive);
      text-decoration: underline;
    }

    &__link {
      margin-left: var(--ds-spacing-inline-xs);
    }
  }

  ::v-deep {
    .v-text-field__details {
      display: none;
    }

    .v-input {
      width: 100%;
      margin-bottom: var(--ds-spacing-inline-sm);

      &__slot {
        margin-bottom: 0 !important;

        input {
          text-overflow: ellipsis;
        }
      }
    }

    .v-tab {
      font-style: normal;
      font-weight: var(--ds-font-weight-semibold);
      font-size: var(--ds-font-size-sm);
      line-height: var(--ds-font-line-height-loose);
      text-transform: none;
      padding: 0 !important;
      letter-spacing: -0.5px;
    }
  }

  .pointer {
    cursor: pointer;
  }

  .image-component {
    width: 100%;
    height: 100%;

    &__container {
      width: calc(100% - 60px);
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &__options-button {
      align-self: flex-start;
    }
  }

  .v-btn {
    max-width: 90%;
    text-align: left;
  }
</style>
